import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';

export default function KeywordOverviewPage() {
  const [params] = useSearchParams();
  const [keyword, setKeyword] = useState(params.get('keyword') || '');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');
  const { updateCredits } = useAuth();

  async function analyze() {
    if (!keyword.trim() || loading) return;
    setError(''); setLoading(true); setData(null);
    try {
      const res = await api.post('/keywords/overview', { keyword });
      setData(res.data.data);
      updateCredits(res.data.credits_remaining);
    } catch (e) { setError(e.response?.data?.error || 'Failed'); } finally { setLoading(false); }
  }

  useEffect(() => { if (params.get('keyword')) analyze(); }, []);

  return (
    <div className="fade-up">
      <div className="page-header">
        <h1 className="page-title">Keyword Overview</h1>
        <p className="page-sub">Volume, KD, CPC, intent, SERP analysis — like SEMrush Keyword Overview · 3 credits</p>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <input className="input input-lg" placeholder="Enter keyword e.g. best seo tools, ai writing" value={keyword} onChange={e => setKeyword(e.target.value)} onKeyDown={e => e.key === 'Enter' && analyze()} />
          <button className="btn btn-orange btn-lg" onClick={analyze} disabled={loading || !keyword.trim()}>{loading ? 'Analyzing...' : 'Search →'}</button>
        </div>
        {error && <div style={{ marginTop: 10, color: '#FF3D57', fontSize: 13, background: '#FFEBEE', padding: '8px 12px', borderRadius: 6 }}>{error}</div>}
      </div>

      {loading && <div className="card"><div className="skeleton" style={{ height: 200 }} /></div>}

      {data && (
        <>
          <div className="metric-grid">
            <div className="card" style={{ margin: 0 }}><div className="metric-label">Volume</div><div className="metric-value">{data.volumeFormatted}</div><div className="metric-sub">global {data.globalVolume.toLocaleString()}</div></div>
            <div className="card" style={{ margin: 0, borderLeft: `4px solid ${data.kdColor}` }}><div className="metric-label">Keyword Difficulty</div><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><div className="metric-value" style={{ color: data.kdColor }}>{data.kd}%</div><span className="badge" style={{ background: `${data.kdColor}15`, color: data.kdColor }}>{data.kdLevel}</span></div><div className="metric-sub">Difficulty level</div></div>
            <div className="card" style={{ margin: 0 }}><div className="metric-label">CPC</div><div className="metric-value">${data.cpc}</div><div className="metric-sub">{data.competitionLevel} competition · {data.competition}</div></div>
            <div className="card" style={{ margin: 0 }}><div className="metric-label">Intent</div><div className="metric-value" style={{ fontSize: 16 }}>{data.intent}</div><div className="metric-sub">search intent</div></div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><span className="card-title">Search Volume Trend (12 months)</span></div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 100 }}>
              {data.trend.map((m,i) => {
                const max = Math.max(...data.trend.map(x => x.volume));
                return <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}><div style={{ width: '100%', background: data.kdColor, height: `${(m.volume/max)*100}%`, borderRadius: '3px 3px 0 0', opacity: 0.8 }}></div><span style={{ fontSize: 9, color: '#6B7280' }}>{m.month.slice(0,3)}</span></div>
              })}
            </div>
          </div>

          <div className="card">
            <div className="tabs">
              <button className={`tab-btn ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>SERP Analysis</button>
              <button className={`tab-btn ${tab === 'related' ? 'active' : ''}`} onClick={() => setTab('related')}>Related Keywords ({data.relatedKeywords.length})</button>
              <button className={`tab-btn ${tab === 'questions' ? 'active' : ''}`} onClick={() => setTab('questions')}>Questions ({data.questions.length})</button>
            </div>

            {tab === 'overview' && (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                  {data.serpAnalysis.features.map(f => (
                    <span key={f.type} className="badge badge-gray">{f.type} × {f.count}</span>
                  ))}
                </div>
                <div className="table-wrap">
                  <table className="table">
                    <thead><tr><th>#</th><th>Domain</th><th>Title</th><th>Traffic</th><th>Backlinks</th></tr></thead>
                    <tbody>
                      {data.serpAnalysis.topResults.map(r => (
                        <tr key={r.position}><td><span className="badge badge-gray">{r.position}</span></td><td style={{ fontWeight: 600 }}>{r.domain}</td><td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</td><td>{r.traffic.toLocaleString()}</td><td>{r.backlinks.toLocaleString()}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {tab === 'related' && (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Keyword</th><th>Volume</th><th>KD</th><th>CPC</th><th>Intent</th></tr></thead>
                  <tbody>
                    {data.relatedKeywords.map((k,i) => (
                      <tr key={i}><td style={{ fontWeight: 600 }}>{k.keyword}</td><td>{k.volume.toLocaleString()}</td><td>{k.kd}%</td><td>${k.cpc}</td><td><span className="badge badge-gray">{k.intent}</span></td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === 'questions' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.questions.map((q,i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: '#FAFBFC', border: '1px solid #F3F4F6', borderRadius: 6 }}>
                    <span style={{ fontWeight: 500 }}>{q.question}</span>
                    <span style={{ color: '#6B7280', fontSize: 12 }}>{q.volume.toLocaleString()} vol · {q.kd}% KD</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
